<template>
  <div>
    <v-snackbar
      v-model="visible"
      bottom
      :timeout="-1"
      class="cookie-banner"
      max-width="960"
    >
      <div class="cookie-banner__content">
        <strong>{{ copy.title }}</strong>
        <p>
          {{ copy.message }}
          <router-link to="/cookies" target="_blank" rel="noopener">
            {{ copy.policyLink }}
          </router-link>
        </p>
      </div>

      <template #action>
        <div class="cookie-banner__actions">
          <v-btn text color="grey lighten-2" @click="reject">
            {{ copy.reject }}
          </v-btn>
          <v-btn text color="grey lighten-2" @click="openSettings">
            {{ copy.configure }}
          </v-btn>
          <v-btn color="primary" depressed @click="acceptAll">
            {{ copy.accept }}
          </v-btn>
        </div>
      </template>
    </v-snackbar>

    <v-dialog v-model="settingsOpen" max-width="720" persistent>
      <v-card class="cookie-settings">
        <v-card-title class="cookie-settings__title">
          {{ copy.settingsTitle }}
        </v-card-title>

        <v-card-text>
          <p class="cookie-settings__intro">
            {{ copy.settingsIntro }}
          </p>

          <v-list two-line>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>{{ copy.necessaryTitle }}</v-list-item-title>
                <v-list-item-subtitle>{{ copy.necessaryText }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-switch :input-value="true" disabled inset />
              </v-list-item-action>
            </v-list-item>

            <v-divider />

            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>{{ copy.analyticsTitle }}</v-list-item-title>
                <v-list-item-subtitle>{{ copy.analyticsText }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-switch v-model="analyticsConsent" color="primary" inset />
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions class="cookie-settings__actions">
          <v-spacer />
          <v-btn text @click="reject">{{ copy.reject }}</v-btn>
          <v-btn text color="primary" @click="saveSettings">{{ copy.save }}</v-btn>
          <v-btn color="primary" depressed @click="acceptAll">{{ copy.accept }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { app } from '@/firebase'

const STORAGE_KEY = 'konvenio_cookie_consent_v1'
const MEASUREMENT_ID = 'G-L4YP40YG5D'

const copies = {
  es: {
    title: 'Usamos cookies en Konvenio',
    message: 'Utilizamos cookies técnicas necesarias y cookies analíticas opcionales para medir el uso del servicio y mejorarlo.',
    policyLink: 'Política de Cookies',
    accept: 'Aceptar todas',
    reject: 'Rechazar no necesarias',
    configure: 'Configurar',
    save: 'Guardar configuración',
    settingsTitle: 'Configuración de cookies',
    settingsIntro: 'Gestiona tus preferencias. Las cookies técnicas son necesarias para iniciar sesión, mantener la seguridad y permitir el funcionamiento básico de Konvenio.',
    necessaryTitle: 'Cookies técnicas necesarias',
    necessaryText: 'Imprescindibles para autenticación, sesión, seguridad y funcionamiento básico. No pueden desactivarse desde este panel.',
    analyticsTitle: 'Cookies analíticas',
    analyticsText: 'Nos ayudan a medir el uso de Konvenio con Firebase Analytics. Solo se activan con tu consentimiento.'
  },
  en: {
    title: 'We use cookies in Konvenio',
    message: 'We use necessary technical cookies and optional analytics cookies to measure service usage and improve it.',
    policyLink: 'Cookie Policy',
    accept: 'Accept all',
    reject: 'Reject non-essential',
    configure: 'Configure',
    save: 'Save settings',
    settingsTitle: 'Cookie settings',
    settingsIntro: 'Manage your preferences. Technical cookies are required for sign-in, security and core Konvenio features.',
    necessaryTitle: 'Necessary technical cookies',
    necessaryText: 'Required for authentication, sessions, security and basic operation. They cannot be disabled from this panel.',
    analyticsTitle: 'Analytics cookies',
    analyticsText: 'They help us measure Konvenio usage with Firebase Analytics. They are only enabled with your consent.'
  },
  pt: {
    title: 'Utilizamos cookies na Konvenio',
    message: 'Utilizamos cookies técnicos necessários e cookies analíticos opcionais para medir a utilização do serviço e melhorá-lo.',
    policyLink: 'Política de Cookies',
    accept: 'Aceitar todos',
    reject: 'Rejeitar não necessários',
    configure: 'Configurar',
    save: 'Guardar configuração',
    settingsTitle: 'Configuração de cookies',
    settingsIntro: 'Gira as suas preferências. Os cookies técnicos são necessários para iniciar sessão, manter a segurança e permitir o funcionamento básico da Konvenio.',
    necessaryTitle: 'Cookies técnicos necessários',
    necessaryText: 'Essenciais para autenticação, sessão, segurança e funcionamento básico. Não podem ser desativados a partir deste painel.',
    analyticsTitle: 'Cookies analíticos',
    analyticsText: 'Ajudam-nos a medir a utilização da Konvenio com Firebase Analytics. Só são ativados com o seu consentimento.'
  }
}

let analyticsInstance = null

export default {
  name: 'CookieBanner',
  data () {
    return {
      visible: false,
      settingsOpen: false,
      analyticsConsent: false
    }
  },
  computed: {
    copy () {
      return copies[this.$i18n.locale] || copies.en
    }
  },
  mounted () {
    window.KonvenioCookies = {
      openSettings: this.openSettings,
      getConsent: this.getConsent,
      resetConsent: this.resetConsent
    }

    const consent = this.getConsent()
    if (consent) {
      this.applyConsent(consent)
      return
    }

    this.disableAnalyticsFlag()
    this.visible = true
  },
  methods: {
    getConsent () {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY))
      } catch (error) {
        return null
      }
    },
    setConsent (analytics) {
      const consent = {
        necessary: true,
        analytics: Boolean(analytics),
        savedAt: new Date().toISOString(),
        version: 1
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(consent))
      this.applyConsent(consent)
      this.visible = false
      this.settingsOpen = false
    },
    acceptAll () {
      this.setConsent(true)
    },
    reject () {
      this.setConsent(false)
    },
    saveSettings () {
      this.setConsent(this.analyticsConsent)
    },
    openSettings () {
      const consent = this.getConsent()
      this.analyticsConsent = Boolean(consent && consent.analytics)
      this.visible = false
      this.settingsOpen = true
    },
    resetConsent () {
      localStorage.removeItem(STORAGE_KEY)
      this.applyConsent({ analytics: false })
      this.visible = true
    },
    async applyConsent (consent) {
      if (consent && consent.analytics) {
        await this.enableAnalytics()
      } else {
        this.disableAnalyticsFlag()
        if (analyticsInstance) {
          const { setAnalyticsCollectionEnabled } = await import('firebase/analytics')
          setAnalyticsCollectionEnabled(analyticsInstance, false)
        }
      }
    },
    async enableAnalytics () {
      window[`ga-disable-${MEASUREMENT_ID}`] = false

      const {
        getAnalytics,
        isSupported,
        setAnalyticsCollectionEnabled
      } = await import('firebase/analytics')

      if (!(await isSupported())) return

      analyticsInstance = analyticsInstance || getAnalytics(app)
      setAnalyticsCollectionEnabled(analyticsInstance, true)
    },
    disableAnalyticsFlag () {
      window[`ga-disable-${MEASUREMENT_ID}`] = true
    }
  }
}
</script>

<style scoped>
.cookie-banner__content {
  max-width: 620px;
}

.cookie-banner__content p {
  margin: 4px 0 0;
  line-height: 1.45;
}

.cookie-banner__content a {
  color: #fff;
  font-weight: 700;
  text-decoration: underline;
}

.cookie-banner__actions,
.cookie-settings__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cookie-settings__title {
  font-weight: 700;
}

.cookie-settings__intro {
  color: rgba(0, 0, 0, 0.68);
  line-height: 1.6;
}

@media (max-width: 600px) {
  .cookie-banner__actions {
    width: 100%;
  }

  .cookie-banner__actions .v-btn {
    flex: 1 1 100%;
  }
}
</style>
